'use client';

/**
 * RevenueRecognitionChart — "When you invoice ≠ when you recognize".
 * Three engagement types (retainer / fixed-price / T&M) on a shared 12-month
 * axis: cumulative billing (dashed) vs recognized revenue (solid), with a
 * balance-sheet band showing the contract liability / asset at each point.
 *
 * Usage:
 *   import RevenueRecognitionChart from './RevenueRecognitionChart';
 *   <RevenueRecognitionChart />
 *
 * No props. Injects its own scoped styles once, on first mount. Uses the app's
 * Poppins font (already loaded globally) — no font <link> injection.
 * Purely presentational — data lives in the ROWS spec below (regenerate
 * variants, incl. zh-hk, by editing the spec). Requires React 16.8+.
 */
import React, { useEffect } from 'react';

/* ---------------------------- Brand assets ----------------------------- */
const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Types -------------------------------- */
type Pt = [number, number]; // [month, cumulative HK$K]
interface RowSpec {
  id: string;
  title: string;
  terms: string;
  billing: Pt[];      // step function (cumulative invoiced)
  recognized: Pt[];   // piecewise-linear (cumulative recognized)
  end: number;        // last month with activity
  note: string;
  noteTone: 'liab' | 'asset' | 'zero';
}

/* -------------------------------- Data --------------------------------- */
const ROWS: RowSpec[] = [
  {
    id: 'retainer',
    title: 'Retainer (stand-ready)',
    terms: 'HK$120K/mo · 12 months · invoiced quarterly in advance',
    billing: [[0, 360], [3, 720], [6, 1080], [9, 1440]],
    recognized: [[0, 0], [12, 1440]],
    end: 12,
    note: 'Each quarterly invoice creates deferred revenue: a contract liability that draws down HK$120K per month as the stand-ready obligation is satisfied.',
    noteTone: 'liab',
  },
  {
    id: 'fixed',
    title: 'Fixed-price project (input method)',
    terms: 'HK$500K · 5-month build + UAT tail · 30% signing / 30% midpoint / 40% completion',
    billing: [[0, 150], [2.5, 300], [5, 500]],
    recognized: [[0, 0], [1, 160], [2, 265], [3, 330], [4, 395], [5, 440], [6, 480], [6.5, 500], [12, 500]],
    end: 6.5,
    note: 'Three beats: the signing invoice creates a brief liability → front-loaded discovery outruns it, flipping to a contract asset (unbilled) → the completion invoice flips it briefly back to a liability until the UAT tail closes.',
    noteTone: 'asset',
  },
  {
    id: 'tm',
    title: 'Time & materials',
    terms: 'HK$1,500/hr · billed monthly for hours logged',
    billing: [[1, 105], [2, 235], [3, 330], [4, 450], [5, 590], [6, 695], [7, 820], [8, 910], [9, 1025], [10, 1160], [11, 1290], [12, 1440]],
    recognized: [[0, 0], [1, 105], [2, 235], [3, 330], [4, 450], [5, 590], [6, 695], [7, 820], [8, 910], [9, 1025], [10, 1160], [11, 1290], [12, 1440]],
    end: 12,
    note: 'Billing catches up to the work every month, so nothing material accrues on the balance sheet. This is the boring case, and it is why the T&M mental model breaks on retainers and fixed-price jobs.',
    noteTone: 'zero',
  },
];

/* ------------------------- Curve evaluation ---------------------------- */
/** Cumulative step function: value holds from each point's month onward. */
function evalStep(pts: Pt[], t: number): number {
  let v = 0;
  for (const [m, c] of pts) { if (t >= m) v = c; else break; }
  return v;
}
/** Piecewise-linear interpolation, clamped flat past the last point. */
function evalLinear(pts: Pt[], t: number): number {
  if (t <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    const [m0, c0] = pts[i - 1], [m1, c1] = pts[i];
    if (t <= m1) return c0 + (c1 - c0) * ((t - m0) / (m1 - m0));
  }
  return pts[pts.length - 1][1];
}

/* ----------------------------- Geometry -------------------------------- */
const PAD_T = 24;

function linePath(pts: Pt[], xOf: (m: number) => number, yOf: (v: number) => number): string {
  return pts.map(([m, v], i) => `${i ? 'L' : 'M'}${xOf(m).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');
}
/** Step path: horizontal hold then vertical rise at each invoice. */
function stepPath(pts: Pt[], xOf: (m: number) => number, yOf: (v: number) => number, end: number): string {
  let d = `M${xOf(pts[0][0]).toFixed(1)},${yOf(0).toFixed(1)}`;
  let prev = 0;
  for (const [m, c] of pts) {
    d += ` L${xOf(m).toFixed(1)},${yOf(prev).toFixed(1)} L${xOf(m).toFixed(1)},${yOf(c).toFixed(1)}`;
    prev = c;
  }
  d += ` L${xOf(Math.max(end, 12)).toFixed(1)},${yOf(prev).toFixed(1)}`;
  return d;
}

const fmtK = (v: number): string => v >= 1000 ? `HK$${(v / 1000).toFixed(2).replace(/\.?0+$/, '')}M` : `HK$${v}K`;

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'rrc-styles';
const CSS = `
.rrc-root { --rrc-font: 'Poppins', system-ui, -apple-system, sans-serif; --rrc-mono: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0F9993; --teal-deep: #0D555C; --teal-tint: #ECFBF9; --teal-line: #CDEAE6;
  --gold: #A8842F; --gold-tint: #F6EFDC; --gold-line: #E7DBB8;
  box-sizing: border-box; width: 100%; max-width: 1160px; margin: 0 auto;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 40px; color: var(--ink);
  font-family: var(--rrc-font); -webkit-font-smoothing: antialiased; }
.rrc-root *, .rrc-root *::before, .rrc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.rrc-root h1, .rrc-root h2, .rrc-root h3, .rrc-root p, .rrc-root img { margin: 0; }
.rrc-root p { letter-spacing: normal; }
/* Isolate from .blog-content prose styles that out-specify the universal reset: leaked h2 margins, the gold ::after underline, and p margins/letter-spacing. */
.rrc-root h1, .rrc-root h2, .rrc-root h3, .rrc-root h4, .rrc-root p { margin: 0; padding: 0; }
.rrc-root p { letter-spacing: normal; }
.rrc-root h1::after, .rrc-root h2::after, .rrc-root h3::after, .rrc-root h1::before, .rrc-root h2::before, .rrc-root h3::before { content: none !important; display: none !important; }
.rrc-root .head { display: flex; justify-content: space-between; align-items: flex-end; gap: 32px; flex-wrap: wrap; }
.rrc-root .head h2 { font-size: 32px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.2; max-width: 620px; }
.rrc-root .head h2 .neq { color: var(--gold); }
.rrc-root .sub { font-size: 14px; color: var(--body); font-style: italic; margin-top: 10px; max-width: 640px; }
.rrc-root .legend { display: flex; flex-direction: column; gap: 8px; flex: none; padding: 14px 18px; border: 1px solid var(--line); border-radius: 10px; background: var(--wash); }
.rrc-root .legend .li { display: flex; align-items: center; gap: 10px; font-size: 12px; font-weight: 500; color: var(--ink-2); }
.rrc-root .legend svg { flex: none; }
.rrc-root .legend .swatch { width: 26px; height: 12px; border-radius: 3px; flex: none; }
.rrc-root .rows { margin-top: 34px; display: flex; flex-direction: column; }
.rrc-root .row { display: grid; grid-template-columns: 250px 1fr; gap: 28px; padding: 26px 0; }
.rrc-root .row + .row { border-top: 1px solid var(--line); }
.rrc-root .meta h2 { font-size: 17px; font-weight: 600; line-height: 1.3; }
.rrc-root .meta .terms { font-family: var(--rrc-mono); font-size: 11px; line-height: 1.6; color: var(--body); margin-top: 8px; }
.rrc-root .meta .note { font-size: 12px; line-height: 1.55; color: var(--body); margin-top: 14px; padding: 10px 12px; border-radius: 8px; }
.rrc-root .meta .note.liab { background: var(--teal-tint); border: 1px solid var(--teal-line); color: var(--teal-deep); }
.rrc-root .meta .note.asset { background: var(--gold-tint); border: 1px solid var(--gold-line); color: #7A5F1D; }
.rrc-root .meta .note.zero { background: var(--wash); border: 1px solid var(--line); }
.rrc-root .charts { display: flex; flex-direction: column; }
.rrc-root .charts .area { flex: 1; min-height: 150px; position: relative; }
.rrc-root .charts .area svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.rrc-root .charts .c-lbl { font-family: var(--rrc-mono); font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; fill: #8A99A3; }
.rrc-root .charts .band-lbl { font-family: var(--rrc-mono); font-size: 9.5px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; }
.rrc-root .charts .band-lbl.halo { paint-order: stroke; stroke: #FFFFFF; stroke-width: 3px; stroke-linejoin: round; }
.rrc-root .charts .amt { font-family: var(--rrc-mono); font-size: 10.5px; font-weight: 600; fill: #3E5460; }
.rrc-root .axis { display: flex; border-top: 2px solid #C6CDD2; padding-top: 7px; }
.rrc-root .axis span { flex: 1; text-align: center; font-family: var(--rrc-mono); font-size: 10px; letter-spacing: 0.04em; color: var(--muted); }
.rrc-root .foot { margin-top: 24px; padding-top: 18px; border-top: 1px solid var(--line); display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; flex-wrap: wrap; }
.rrc-root .foot .cap { font-size: 12px; line-height: 1.5; color: var(--body); font-style: italic; flex: 0 1 75%; max-width: 75%; }
.rrc-root .rrc-logo { height: 34px; width: auto; display: block; flex: none; }
@media (max-width: 860px) {
  .rrc-root { padding: 28px 22px 30px; }
}
.rrc-root.compact { padding: 26px 0 26px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.rrc-root.compact .head h2 { font-size: 22px; }
.rrc-root.compact .sub { font-size: 13px; }
.rrc-root.compact .head { gap: 18px; }
.rrc-root.compact .legend { flex-direction: row; flex-wrap: wrap; gap: 8px 18px; width: 100%; padding: 12px 14px; }
.rrc-root.compact .rows { margin-top: 22px; }
.rrc-root.compact .row { grid-template-columns: 1fr; gap: 14px; padding: 20px 0; }
.rrc-root.compact .meta .note { margin-top: 10px; }
.rrc-root.compact .axis span { font-size: 8.5px; letter-spacing: 0; }
.rrc-root.compact .foot { flex-direction: column; align-items: stretch; gap: 14px; }
.rrc-root.compact .foot .cap { flex: none; max-width: 100%; }
.rrc-root.compact .rrc-logo { align-self: flex-end; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ------------------------------ Row chart ------------------------------ */
function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

const RowChart: React.FC<{ row: RowSpec; inView: boolean; delay: number }> = ({ row, inView, delay }) => {
  const areaRef = React.useRef<HTMLDivElement>(null);
  const [dims, setDims] = React.useState<[number, number]>([760, 170]);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const measure = (): void => {
      const r = el.getBoundingClientRect();
      if (r.width > 40 && r.height > 40) setDims([Math.round(r.width), Math.round(r.height)]);
    };
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);
  const [CW, H] = dims;
  const CH = H - 2;
  const x = (m: number): number => (m / 12) * CW;
  const maxV = Math.max(row.billing[row.billing.length - 1][1], row.recognized[row.recognized.length - 1][1]);
  const yOf = (v: number): number => PAD_T + (CH - PAD_T - 2) * (1 - v / maxV);
  const isZero = row.noteTone === 'zero';
  const total = row.billing[row.billing.length - 1][1];
  return (
    <div className="charts">
      <div className="area" ref={areaRef}>
      <svg viewBox={`0 0 ${CW} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${row.title}: cumulative billing vs recognized revenue over 12 months`}>
        {/* month gridlines */}
        {Array.from({ length: 11 }, (_, i) => (
          <line key={i} x1={x(i + 1)} x2={x(i + 1)} y1={PAD_T} y2={CH} stroke="#EEF1F2" strokeWidth="1" />
        ))}
        <line x1="0" x2={CW} y1={yOf(total)} y2={yOf(total)} stroke="#E4E7EA" strokeWidth="1" strokeDasharray="2 4" />
        <text className="amt" x={CW - 4} y={yOf(total) - 5} textAnchor="end">{fmtK(total)}</text>
        {/* billing (dashed; T&M bills continuously so it hugs recognition) + recognition (solid) */}
        {/* solid recognition first, dashed billing on top so converged stretches still read as two lines */}
        <path pathLength={1} d={linePath(row.recognized, x, yOf)} fill="none" stroke="#0F9993" strokeWidth="2.75" strokeLinejoin="round" strokeLinecap="round" style={{ strokeDasharray: 1, strokeDashoffset: inView ? 0 : 1, transition: `stroke-dashoffset .9s cubic-bezier(0.19,1,0.22,1) ${delay}s` }} />
        <g style={{ opacity: inView ? 1 : 0, transition: `opacity .5s ease ${delay + 0.5}s` }}>
          <path d={isZero ? linePath([[0, 0], ...row.billing], x, yOf) : stepPath(row.billing, x, yOf, row.end)} fill="none" stroke="#3E5460" strokeWidth="2" strokeDasharray="6 5" strokeLinejoin="round" />
          {isZero ? null : row.billing.map(([m, c]) => (
            <circle key={m} cx={x(m)} cy={yOf(c)} r="3.5" fill="#fff" stroke="#3E5460" strokeWidth="2" />
          ))}
        </g>
      </svg>
      </div>
      <div className="axis">{Array.from({ length: 12 }, (_, i) => <span key={i}>M{i + 1}</span>)}</div>
    </div>
  );
};

/* ------------------------------ Component ------------------------------ */
const RevenueRecognitionChart: React.FC = () => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef);
  const [compact, setCompact] = React.useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setCompact(el.offsetWidth < 640);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);
  return (
    <div className={`rrc-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <div>
          <h2>When you invoice <span className="neq">≠</span> when you recognize</h2>
          <p className="sub">Under HKFRS 15, revenue follows the pattern of the work, not the invoice date. Three common engagement types break that link in three different ways.</p>
        </div>
        <div className="legend">
          <div className="li"><svg width="26" height="12"><line x1="0" y1="6" x2="26" y2="6" stroke="#3E5460" strokeWidth="2" strokeDasharray="6 5" /></svg>Invoiced (cumulative)</div>
          <div className="li"><svg width="26" height="12"><line x1="0" y1="6" x2="26" y2="6" stroke="#0F9993" strokeWidth="2.75" /></svg>Revenue recognized</div>
        </div>
      </div>
      <div className="rows">
        {ROWS.map((row, i) => (
          <div className="row" key={row.id}>
            <div className="meta">
              <h2>{row.title}</h2>
              <p className="terms">{row.terms}</p>
              <p className={`note ${row.noteTone}`}>{row.note}</p>
            </div>
            <RowChart row={row} inView={inView} delay={i * 0.15} />
          </div>
        ))}
      </div>
      <div className="foot">
        <p className="cap">The gap between the dashed and solid lines is what sits on the balance sheet. T&M keeps them glued together, which is exactly why the T&M mental model fails the moment a firm runs a retainer or a fixed-price build.</p>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="rrc-logo" src={PSGLOBAL_LOGO} alt="PS Global" />
      </div>
    </div>
  );
};

export default RevenueRecognitionChart;
